import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import { brand, notice } from "../../theme/businessFlowsTheme";

// The page's own font for anything that is meant to be typed or pasted.
export const mono = '"SFMono-Regular", Menlo, Consolas, "Liberation Mono", monospace';

// The key in every example. Written out in full, and with nothing in it a
// request header can't carry: a reader who copies an example and pastes it
// into the check above gets a plain "this key isn't valid" from the server,
// not a browser that refuses to send it.
export const SAMPLE_KEY = "bfb_live_ab23cdef_kqp9t2wv7m";

// The portal keeps one rule about its two materials, and keeps it everywhere:
// dark is code you will copy, light is something you read or do. So the dark
// surface below belongs to code blocks alone - not to a form, not to a
// refusal - and the page reads as paper with listings set into it rather than
// as a console.
export const dev = {
  code: "#171733",
  codeBar: "#20203d",
  codeLine: "rgba(255, 255, 255, 0.12)",
  codeText: "#e9ebf4",
  codeMuted: "#a8aec6",
  token: {
    comment: "#8189a6",
    string: "#e6dd97",
    keyword: "#9fb2ff",
    number: "#f0b49f",
    key: "#9fb2ff",
  },
};

// One type scale for the whole portal, so a heading is a heading wherever it
// is. The measure is in pixels rather than `ch`: Geist's "0" is wide, and
// 72ch of it runs to ninety-odd characters a line, which is a paragraph
// nobody finishes.
export const measure = 580;

export const type = {
  sectionTitle: { fontSize: { xs: "1.3rem", md: "1.45rem" }, fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.2 },
  subTitle: { fontSize: "1.02rem", fontWeight: 650, letterSpacing: "-0.012em", lineHeight: 1.35 },
  prose: { fontSize: "0.975rem", lineHeight: 1.72, color: brand.prose, maxWidth: measure },
  meta: { fontSize: "0.8rem", lineHeight: 1.55, color: brand.muted },
  code: { fontFamily: `${mono} !important`, fontSize: { xs: "0.74rem", sm: "0.8rem" }, lineHeight: 1.8 },
};

// Prose that keeps the portal's measure and colour without repeating itself.
export function Prose({ children, sx }) {
  return <Typography component="p" sx={{ ...type.prose, ...sx }}>{children}</Typography>;
}

const codeStyle = {
  fontFamily: `${mono} !important`,
  fontSize: "0.85em",
  bgcolor: brand.shell,
  border: `1px solid ${brand.line}`,
  borderRadius: "6px",
  px: 0.55,
  py: 0.15,
  color: brand.inkSoft,
  whiteSpace: "nowrap",
};

// Inline code that keeps its whole text in one node, so it reads as one phrase.
export function Code({ children }) {
  return (
    <Box component="code" sx={codeStyle}>
      {children}
    </Box>
  );
}

// ── Status and method, coloured the same way everywhere on the page ────────
// Green: it worked. Clay: change something and call again. Amber: wait, then
// call again. Slate: nothing to change, the server is having a moment. The
// tones come from the product's own notice palette, so a refusal here looks
// like a refusal anywhere else in the app.
export const statusTone = (status) => {
  const code = Number(status);
  if (code >= 200 && code < 300) return notice.good;
  if (code === 429 || code === 402) return notice.wait;
  if (code >= 500) return notice.quiet;
  if (code >= 400) return notice.fix;
  return notice.quiet;
};

// The portal's notices are the app's notices; it only chooses the tone.
export { Notice } from "../workspaces/WorkspaceUi";

export function StatusChip({ status }) {
  const tone = statusTone(status);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        fontFamily: `${mono} !important`,
        fontSize: "0.78rem",
        fontWeight: 700,
        lineHeight: 1.6,
        px: 0.9,
        borderRadius: "6px",
        color: tone.fg,
        bgcolor: tone.bg,
        border: `1px solid ${tone.line}`,
      }}
    >
      {status}
    </Box>
  );
}

// Only GET exists today; the shape is here so a second method reads the same.
// Outlined rather than filled: the endpoint list repeats it a dozen times, and
// a dozen navy slabs make a list look like a warning.
export function MethodChip({ method = "GET", onDark = false }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        minWidth: 46,
        textAlign: "center",
        fontFamily: `${mono} !important`,
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        lineHeight: 1.85,
        px: 0.75,
        borderRadius: "6px",
        color: onDark ? dev.codeText : brand.inkSoft,
        bgcolor: onDark ? "rgba(255,255,255,0.10)" : "#eef0f5",
        border: `1px solid ${onDark ? dev.codeLine : brand.line}`,
      }}
    >
      {String(method).toUpperCase()}
    </Box>
  );
}

// ── A section of the guide, and its place in the contents rail ─────────────
export function Section({ id, title, subtitle, action, children, sx }) {
  return (
    <Box component="section" id={id} tabIndex={-1} sx={{ mb: { xs: 5.5, md: 9 }, scrollMarginTop: "16px", outline: "none", ...sx }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "flex-start", sm: "flex-end" }}
        justifyContent="space-between"
        sx={{ mb: { xs: 2.25, md: 3 } }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h2" component="h2" sx={type.sectionTitle}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ ...type.prose, fontSize: "0.925rem", color: brand.muted, mt: 0.75 }}>{subtitle}</Typography>
          )}
        </Box>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

// A heading inside a section, for a point that deserves its own line.
export function SubHeading({ children, sx }) {
  return (
    <Typography variant="h3" component="h3" sx={{ ...type.subTitle, mb: 1, ...sx }}>
      {children}
    </Typography>
  );
}

// ── Code, with just enough colour to read it ───────────────────────────────
const WORDS = {
  javascript: /^(?:const|let|var|await|async|function|return|if|else|for|while|do|new|of|in|throw|try|catch|null|true|false|undefined)\b/,
  python: /^(?:import|from|as|def|return|raise|if|elif|else|for|while|in|not|and|or|None|True|False|print)\b/,
  bash: /^(?:curl|export|echo|while|do|done|if|then|fi)\b/,
  json: /^(?:true|false|null)\b/,
  http: /^(?:GET|POST|PUT|DELETE|HTTP)\b/,
};

const STRING = /^(?:"(?:[^"\\\n]|\\.)*"?|'(?:[^'\\\n]|\\.)*'?|`(?:[^`\\]|\\.)*`?)/;
const NUMBER = /^\d+(?:\.\d+)?/;
const WORD = /^[A-Za-z_$][\w$-]*/;
const SPACE = /^\s+/;

// A small scanner rather than a highlighting library: five classes is all this
// page shows, and a library would cost more than the whole portal.
export function tokenize(code, language = "bash") {
  const words = WORDS[language] || WORDS.bash;
  const out = [];
  const push = (text, cls) => {
    const last = out[out.length - 1];
    if (last && last.cls === cls) last.text += text;
    else out.push({ text, cls });
  };
  let rest = String(code || "");
  let previous = "\n";
  while (rest) {
    const lineComment = (language === "javascript" && rest.startsWith("//") && !previous.match(/[:\w]/)) || ((language === "python" || language === "bash") && rest[0] === "#");
    if (lineComment) {
      const end = rest.indexOf("\n");
      const text = end === -1 ? rest : rest.slice(0, end);
      push(text, "comment");
      rest = end === -1 ? "" : rest.slice(end);
      previous = "\n";
      continue;
    }
    const string = STRING.exec(rest);
    if (string) {
      const after = rest.slice(string[0].length);
      push(string[0], language === "json" && /^\s*:/.test(after) ? "key" : "string");
      rest = after;
      previous = string[0].slice(-1);
      continue;
    }
    const space = SPACE.exec(rest);
    if (space) {
      push(space[0], "plain");
      rest = rest.slice(space[0].length);
      previous = " ";
      continue;
    }
    const word = WORD.exec(rest);
    if (word) {
      push(word[0], words.test(word[0]) ? "keyword" : "plain");
      rest = rest.slice(word[0].length);
      previous = word[0].slice(-1);
      continue;
    }
    const number = NUMBER.exec(rest);
    if (number) {
      // A number inside a command or an address is part of it, not a value.
      push(number[0], language === "bash" || language === "http" ? "plain" : "number");
      rest = rest.slice(number[0].length);
      previous = number[0].slice(-1);
      continue;
    }
    push(rest[0], "plain");
    previous = rest[0];
    rest = rest.slice(1);
  }
  return out;
}

function Highlighted({ code, language }) {
  const tokens = useMemo(() => tokenize(code, language), [code, language]);
  return (
    <>
      {tokens.map((token, index) =>
        token.cls === "plain" ? (
          <React.Fragment key={index}>{token.text}</React.Fragment>
        ) : (
          <Box component="span" key={index} sx={{ color: dev.token[token.cls] }}>
            {token.text}
          </Box>
        )
      )}
    </>
  );
}

function CopyButton({ text, label = "Copy" }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      // No clipboard permission: the text is on screen and can be selected.
    }
  };
  return (
    <Button
      size="small"
      onClick={copy}
      aria-label={label}
      startIcon={copied ? <DoneRoundedIcon fontSize="small" /> : <ContentCopyRoundedIcon fontSize="small" />}
      sx={{
        flexShrink: 0,
        minWidth: 0,
        px: 1,
        fontSize: "0.76rem",
        fontWeight: 600,
        color: copied ? brand.signal : dev.codeMuted,
        "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
        "&.Mui-focusVisible": { outline: `2px solid ${brand.signal}`, outlineOffset: 2 },
      }}
    >
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

// One block of code on the portal's dark material: what it is on the left (a
// caption, or the language tabs), a copy button that says it copied on the
// right.
export function CodeBlock({ code, language = "bash", caption, header, label = "Copy the example", sx }) {
  return (
    <Box
      sx={{
        borderRadius: "14px",
        overflow: "hidden",
        bgcolor: dev.code,
        border: `1px solid ${dev.code}`,
        ...sx,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pl: header ? 0.5 : 1.75, pr: 1, py: header ? 0 : 0.5, bgcolor: dev.codeBar, borderBottom: `1px solid ${dev.codeLine}` }}
      >
        {header || (
          <Typography component="span" sx={{ fontFamily: `${mono} !important`, fontSize: "0.72rem", color: dev.codeMuted, letterSpacing: "0.02em" }}>
            {caption || language}
          </Typography>
        )}
        <CopyButton text={code} label={label} />
      </Stack>
      <Box
        component="pre"
        sx={{
          m: 0,
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.5, sm: 1.75 },
          color: dev.codeText,
          overflowX: "auto",
          whiteSpace: "pre",
          ...type.code,
        }}
      >
        <Highlighted code={code} language={language} />
      </Box>
    </Box>
  );
}

// The same call in each language a reader is likely to reach for, chosen from
// the head of the block itself rather than from a strip floating above it.
export function CodeTabs({ samples }) {
  const [tab, setTab] = useState(0);
  const index = Math.min(tab, samples.length - 1);
  const current = samples[index];
  const tabs = (
    <Tabs
      value={index}
      onChange={(_, next) => setTab(next)}
      variant="scrollable"
      scrollButtons={false}
      aria-label="The same call, in three languages"
      sx={{
        minHeight: 38,
        // index.css paints every tab strip in the app white; this one lives on
        // the code block's own dark head.
        "& .MuiTabs-flexContainer": { backgroundColor: "transparent", color: "inherit" },
        "& .MuiTab-root": {
          minHeight: 38,
          py: 0,
          px: 1.5,
          fontSize: "0.8rem",
          fontWeight: 600,
          color: dev.codeMuted,
          letterSpacing: 0,
        },
        "& .Mui-selected": { color: "#fff !important" },
        "& .Mui-focusVisible": { outline: `2px solid ${brand.signal}`, outlineOffset: -2, borderRadius: "8px" },
        "& .MuiTabs-indicator": { backgroundColor: brand.signal, height: 2 },
      }}
    >
      {samples.map((sample) => (
        <Tab key={sample.label} label={sample.label} />
      ))}
    </Tabs>
  );
  return (
    <CodeBlock code={current.code} language={current.language} header={tabs} label={`Copy the ${current.label} example`} />
  );
}
